import React, { useEffect, useState } from "react";
import { Cards } from "../components/Cards.tsx";
import { useParams } from "react-router-dom";
import { getStaff } from "../services/users.js";
import {
  capitalize,
  dateDefaultValue,
  dateFormat,
  initials,
} from "../utils/functions.js";
import { TeamMemberType } from "../utils/types.ts";
import InputField from "../components/Forms/InputField.js";
import { useForm } from "react-hook-form";
import SelectField from "../components/Forms/SelectField.js";
import Spinner from "../components/PageLoader.js";

export const StaffDetails: React.FC = () => {
  const [editContact, setEditContact] = useState(false);
  const [editEmployment, setEditEmployment] = useState(false);
  const [user, setUser] = useState<TeamMemberType | null>(null);

  const urlParam = useParams();

  const fetchUser = async () => {
    try {
      const { data } = await getStaff(urlParam.id);
      setUser(data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm();

  const onSubmit = (e) => {
    const payload = {
      ...user,
      ...e,
    };
    console.log(payload);
  };

  return (
    <>
      {user ? (
        <div className="staff-details">
          {/* Header Section with Enhanced Design */}
          <Cards classes="mb-24 staff-details__header">
            <div className="staff-details__header-content">
              <div className="staff-details__title-section">
                <div className="staff-details__icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" fill="white"/>
                    <path d="M12 14C7.58172 14 4 17.5817 4 22H20C20 17.5817 16.4183 14 12 14Z" fill="white"/>
                  </svg>
                </div>
                <div>
                  <h4 className="staff-details__title">Staff Member Profile</h4>
                  <p className="staff-details__subtitle">
                    {`${user?.firstName} ${user?.lastName}`} • {dateFormat(user?.startDate, "dd MMMM yyyy")}
                  </p>
                </div>
              </div>
              <div className="staff-details__avatar-section">
                <div className="staff-details__avatar-circle">
                  {initials(user?.firstName, user?.lastName)}
                </div>
              </div>
            </div>
          </Cards>

          {/* Personal Information Section */}
          <Cards classes="mb-24 staff-details__personal" edit setEdit={() => setEditContact(!editContact)}>
            <div className="staff-details__section-header">
              <div className="staff-details__section-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" fill="var(--primary)"/>
                  <path d="M12 14C7.58172 14 4 17.5817 4 22H20C20 17.5817 16.4183 14 12 14Z" fill="var(--primary)"/>
                </svg>
              </div>
              <h6>Personal Information</h6>
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="staff-details__info-grid">
                {!editContact ? (
                  <div className="staff-details__info-item">
                    <label>Date Of Birth</label>
                    <p className="staff-details__info-value">
                      {dateFormat(user?.dateOfBirth, "do MMM, yyyy") || "N/A"}
                    </p>
                  </div>
                  ) : (
                    <InputField
                      defaultValue={dateDefaultValue(user?.dateOfBirth)}
                      name="dateOfBirth"
                      control={control}
                      label="Date Of Birth"
                      type="date"
                      error={errors["dateOfBirth"]?.message}
                    />
                  )}
                  {!editContact ? (
                  <div className="staff-details__info-item">
                    <label>Phone</label>
                    <p className="staff-details__info-value">{user?.telephoneNumber || "N/A"}</p>
                  </div>
                  ) : (
                    <InputField
                      defaultValue={user?.telephoneNumber}
                      name="telephoneNumber"
                      control={control}
                      label="Phone Number"
                      type="text"
                      error={errors["telephoneNumber"]?.message}
                    />
                  )}
                  {!editContact ? (
                  <div className="staff-details__info-item">
                      <label>Email</label>
                    <p className="staff-details__info-value">{user?.emailAddress || "N/A"}</p>
                    </div>
                  ) : (
                    <InputField
                      defaultValue={user?.emailAddress}
                      name="emailAddress"
                      control={control}
                      label="Email"
                      type="email"
                      error={errors["emailAddress"]?.message}
                    />
                  )}
                  {!editContact ? (
                  <div className="staff-details__info-item">
                      <label>Address</label>
                    <p className="staff-details__info-value">{user?.address || "N/A"}</p>
                    </div>
                  ) : (
                    <InputField
                      defaultValue={user?.address}
                      name="address"
                      control={control}
                      label="Address"
                      type="text"
                      error={errors["address"]?.message}
                    />
                  )}

                  {!editContact ? (
                  <div className="staff-details__info-item">
                      <label>Postal Code</label>
                    <p className="staff-details__info-value">{user?.postalCode || "N/A"}</p>
                    </div>
                  ) : (
                    <InputField
                      defaultValue={user?.postalCode}
                      name="postalCode"
                      control={control}
                      label="Postal Code"
                      type="text"
                      error={errors["postalCode"]?.message}
                    />
                  )}

                  {!editContact ? (
                  <div className="staff-details__info-item">
                      <label>Gender</label>
                    <p className="staff-details__info-value">{capitalize(user?.gender) || "N/A"}</p>
                    </div>
                  ) : (
                    <SelectField
                      control={control}
                      label="Gender"
                      name="gender"
                      defaultValue={user?.gender}
                      options={[
                        { label: "Male", value: "male" },
                        { label: "Female", value: "female" },
                        { label: "Other", value: "other" },
                      ]}
                      error={errors["gender"]?.message}
                    />
                  )}

                  {!editContact ? (
                  <div className="staff-details__info-item">
                      <label>Ethnicity</label>
                    <p className="staff-details__info-value">{user?.ethnicity || "N/A"}</p>
                    </div>
                  ) : (
                    <InputField
                      defaultValue={user?.ethnicity}
                      name="ethnicity"
                      control={control}
                      label="Ethnicity"
                      type="text"
                      error={errors["ethnicity"]?.message}
                    />
                  )}

                  {!editContact ? (
                  <div className="staff-details__info-item">
                      <label>Start Date</label>
                    <p className="staff-details__info-value">
                      {dateFormat(user?.startDate, "do MMM, yyyy") || "N/A"}
                    </p>
                    </div>
                  ) : (
                    <InputField
                      defaultValue={dateDefaultValue(user?.startDate)}
                      name="startDate"
                      control={control}
                      label="Start Date"
                      type="date"
                      error={errors["startDate"]?.message}
                    />
                  )}
                </div>

              {editContact && (
                <div className="staff-details__actions">
                  <button
                    onClick={() => setEditContact(false)}
                    type="button"
                    className="staff-details__action-btn staff-details__action-btn--secondary"
                  >
                    Close
                  </button>
                  <button
                    className="staff-details__action-btn staff-details__action-btn--primary"
                    type="submit"
                  >
                    Save
                  </button>
                </div>
              )}
              </form>
            </Cards>

          {/* Employment Details Section */}
          <Cards classes="mb-24 staff-details__employment" edit setEdit={() => setEditEmployment(!editEmployment)}>
            <div className="staff-details__section-header">
              <div className="staff-details__section-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M12 7V3H2V21H22V9H12V7ZM6 19H4V17H6V19ZM6 15H4V13H6V15ZM6 11H4V9H6V11ZM6 7H4V5H6V7ZM10 19H8V17H10V19ZM10 15H8V13H10V15ZM10 11H8V9H10V11ZM10 7H8V5H10V7ZM20 19H12V11H20V19ZM18 13H14V15H18V13ZM18 17H14V19H18V17Z" fill="var(--primary)"/>
                </svg>
              </div>
              <h6>Employment Details</h6>
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="staff-details__info-grid">
                  {!editEmployment ? (
                  <div className="staff-details__info-item">
                      <label>Role</label>
                    <p className="staff-details__info-value">{user?.role || "N/A"}</p>
                    </div>
                  ) : (
                    <InputField
                      name="role"
                      control={control}
                      label="Role"
                      type="text"
                      error={errors["role"]?.message}
                    />
                  )}

                  {!editEmployment ? (
                  <div className="staff-details__info-item">
                      <label>Manager</label>
                    <p className="staff-details__info-value">{user?.manager || "N/A"}</p>
                    </div>
                  ) : (
                    <InputField
                      name="manager"
                      control={control}
                      label="Manager"
                      type="text"
                      error={errors["manager"]?.message}
                    />
                  )}

                  {!editEmployment ? (
                  <div className="staff-details__info-item">
                      <label>Permission</label>
                    <p className="staff-details__info-value">{user?.permission || "N/A"}</p>
                    </div>
                  ) : (
                    <InputField
                      name="permission"
                      control={control}
                      label="Permission"
                      type="text"
                      error={errors["permission"]?.message}
                    />
                  )}

                  {!editEmployment ? (
                  <div className="staff-details__info-item">
                      <label>Login Code</label>
                    <p className="staff-details__info-value">{user?.logincode || "N/A"}</p>
                    </div>
                  ) : (
                    <InputField
                      name="logincode"
                      control={control}
                      label="Login Code"
                      type="text"
                      error={errors["logincode"]?.message}
                    />
                  )}
                </div>

              {editEmployment && (
                <div className="staff-details__actions">
                  <button
                    onClick={() => setEditEmployment(false)}
                    type="button"
                    className="staff-details__action-btn staff-details__action-btn--secondary"
                  >
                    Close
                  </button>
                  <button
                    className="staff-details__action-btn staff-details__action-btn--primary"
                    type="submit"
                  >
                    Save
                  </button>
                </div>
              )}
              </form>
            </Cards>
        </div>
      ) : (
        <Spinner />
      )}
    </>
  );
};
